import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Send, X, Mail, Users, AlertCircle, UserCheck } from 'lucide-react';
import { previewRaffleEmail } from '../../lib/api';

/**
 * Email preview modal — shows the rendered HTML, lets the admin choose
 * the audience (raffle participants vs all registered users) and confirms
 * the send.
 *
 * Props:
 *  - open, onClose
 *  - raffle: { _id, title }
 *  - emailType: 'launch' | 'urgency' | 'soldout' | 'drawdate'
 *  - onSend: (audience) => Promise<void>
 *  - typeLabel, typeIcon
 */
export default function EmailPreviewDialog({ open, onClose, raffle, emailType, onSend, typeLabel, typeIcon, ticketsOverride = null }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [audience, setAudience] = useState(null); // 'participants' | 'all_users' (resolved by server initially)

  // Initial load
  useEffect(() => {
    if (!open || !raffle?._id || !emailType) {
      setPreviewData(null); setError(null); setAudience(null); return;
    }
    let cancelled = false;
    setLoading(true); setError(null);
    previewRaffleEmail(raffle._id, emailType, 'default', ticketsOverride)
      .then((res) => {
        if (cancelled) return;
        setPreviewData(res.data);
        setAudience(res.data?.audience_kind || 'participants');
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Erreur de chargement de la prévisualisation');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, raffle?._id, emailType, ticketsOverride]);

  // Reload when audience changes (counts may differ + label too)
  const switchAudience = async (newAud) => {
    if (newAud === audience) return;
    setAudience(newAud);
    setLoading(true); setError(null);
    try {
      const res = await previewRaffleEmail(raffle._id, emailType, newAud, ticketsOverride);
      setPreviewData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!onSend) return;
    setSending(true);
    try {
      await onSend(audience);
      onClose?.();
    } finally {
      setSending(false);
    }
  };

  const available = previewData?.audiences_available || {};
  const participantsCount = available.participants ?? 0;
  const allUsersCount = available.all_users ?? 0;
  const currentCount = previewData?.audience_count ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0 !bg-white border-2 border-[hsl(var(--gold)/0.4)]" data-testid="email-preview-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[hsl(var(--gold)/0.2)] shrink-0 bg-white">
          <DialogTitle className="flex items-center gap-2 text-[#0E4936] font-semibold text-xl">
            {typeIcon || <Mail className="w-5 h-5 text-[#A07828]" />}
            Prévisualisation — {typeLabel || 'Email'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#0E4936]/70 leading-relaxed">
            Vérifiez le contenu exact que vos clients vont recevoir, choisissez l'audience puis cliquez « Envoyer ».
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-white" data-testid="email-preview-body">
          {/* Audience selector */}
          {previewData && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="audience-selector">
              <button
                type="button"
                onClick={() => switchAudience('participants')}
                disabled={participantsCount === 0}
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  audience === 'participants'
                    ? 'border-[#A07828] bg-[#FEF3C7] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-[#A07828]/40'
                } ${participantsCount === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="audience-participants"
              >
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck className={`w-4 h-4 ${audience === 'participants' ? 'text-[#A07828]' : 'text-gray-500'}`} />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#0E4936]/70">Option 1 — Ciblée</span>
                </div>
                <p className="text-sm font-bold text-[#0E4936]">Participants de ce tirage</p>
                <p className="text-xs text-[#0E4936]/65 mt-0.5">
                  <span className="font-mono font-bold text-[#A07828]">{participantsCount}</span> personne{participantsCount !== 1 ? 's' : ''} ont acheté un ticket
                </p>
              </button>
              <button
                type="button"
                onClick={() => switchAudience('all_users')}
                disabled={allUsersCount === 0}
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  audience === 'all_users'
                    ? 'border-[#A07828] bg-[#FEF3C7] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-[#A07828]/40'
                } ${allUsersCount === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                data-testid="audience-all-users"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users className={`w-4 h-4 ${audience === 'all_users' ? 'text-[#A07828]' : 'text-gray-500'}`} />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#0E4936]/70">Option 2 — Massive</span>
                </div>
                <p className="text-sm font-bold text-[#0E4936]">Tous les inscrits</p>
                <p className="text-xs text-[#0E4936]/65 mt-0.5">
                  <span className="font-mono font-bold text-[#A07828]">{allUsersCount}</span> client{allUsersCount !== 1 ? 's' : ''} actif{allUsersCount !== 1 ? 's' : ''} dans la base
                </p>
              </button>
            </div>
          )}

          {/* Subject */}
          {previewData && !error && (
            <div className="p-3 rounded-xl bg-[#F8FAFC] border border-gray-200" data-testid="email-preview-subject">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Sujet du mail</p>
              <p className="text-sm font-semibold text-[#0E4936] break-words leading-snug">{previewData.subject}</p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#0E4936]/70 gap-2 bg-white">
              <Loader2 className="w-5 h-5 animate-spin" />
              Génération de la prévisualisation…
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">{error}</div>
            </div>
          )}

          {/* Iframe preview */}
          {!loading && !error && previewData && (
            <div className="rounded-xl overflow-hidden border-2 border-[hsl(var(--gold)/0.3)] bg-[#0c3d2c]">
              <div className="px-3 py-2 bg-[#0E4936] text-[#F5EFE0] text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
                <Mail className="w-3 h-3" /> Aperçu du mail (rendu HTML exact)
              </div>
              <iframe
                srcDoc={previewData.html}
                title="Email preview"
                className="w-full bg-white"
                style={{ height: '520px', border: 'none' }}
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                data-testid="email-preview-iframe"
              />
              <p className="px-3 py-1.5 bg-amber-50 border-t border-amber-200 text-[10px] text-amber-800 italic flex items-center gap-1.5">
                💡 Note : dans la prévisualisation, les liens s'ouvrent dans un nouvel onglet. Dans les vrais emails reçus par les clients, ils fonctionneront normalement.
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[hsl(var(--gold)/0.2)] px-6 py-3 flex items-center justify-between gap-3 bg-[#FAF8F1] shrink-0">
          <Badge variant="outline" className="border-[#A07828]/40 text-[#0E4936] bg-white text-xs">
            🎯 {raffle?.title}
          </Badge>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={sending}
              className="border-[#0E4936]/30 text-[#0E4936] hover:bg-[#0E4936]/5 gap-1.5 bg-white"
              data-testid="email-preview-cancel"
            >
              <X className="w-4 h-4" /> Annuler
            </Button>
            <Button
              onClick={handleSend}
              disabled={loading || sending || !!error || !previewData || currentCount === 0}
              className="bg-gradient-to-r from-[#D4AF37] to-[#A07828] text-[#0E4936] hover:opacity-90 gap-1.5 min-w-[220px] font-bold"
              data-testid="email-preview-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending
                ? 'Envoi en cours…'
                : currentCount === 0
                  ? 'Aucun destinataire'
                  : `Envoyer à ${currentCount} ${audience === 'all_users' ? 'inscrit' : 'participant'}${currentCount > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
